import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  Sync as SyncIcon,
  DataUsage as DataUsageIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import accountingService from './services/accountingService';
import realDataAccountingService from './services/realDataAccountingService';
import {
  setChartOfAccounts,
  setAccountsLoading,
  setAccountsError,
  setFinancialPeriods,
  setPeriodsLoading,
  setPeriodsError,
  setSelectedTab
} from './redux/accounting';

// Import accounting components
import AccountingDashboard from './components/accounting/AccountingDashboard';
import ComprehensiveAccountingDashboard from './components/accounting/ComprehensiveAccountingDashboard';
import TrialBalance from './components/accounting/TrialBalance';
import BalanceSheet from './components/accounting/BalanceSheet';
import IncomeStatement from './components/accounting/IncomeStatement';
import JournalEntries from './components/accounting/JournalEntries';
import AccountingIntegration from './components/accounting/AccountingIntegration';
import DataDebugger from './components/accounting/DataDebugger';
import DataTest from './components/accounting/DataTest';
import DataInspector from './components/accounting/DataInspector';
import CashFlowStatement from './components/accounting/CashFlowStatement';
import GeneralLedger from './components/accounting/GeneralLedger';
import DataTestComponent from './components/accounting/DataTestComponent';
import DataInitializer from './components/accounting/DataInitializer';
import DataLoader from './components/accounting/DataLoader';
import ApiTester from './components/accounting/ApiTester';

const Accounting = () => {
  const dispatch = useDispatch();
  const { 
    selectedTab, 
    chartOfAccounts, 
    accountsLoading, 
    accountsError,
    financialPeriods,
    periodsLoading,
    periodsError
  } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadInitialData();
    }
  }, [currentSchool]);

  // Get Redux data at component level - using correct structure
  const reduxData = {
    students: useSelector(state => state.students) || [],
    teachers: useSelector(state => state.teachers) || [],
    expenses: useSelector(state => state.expenses) || [],
    sales: useSelector(state => state.sales) || [],
    assets: useSelector(state => state.assets) || [],
    class: useSelector(state => state.class) || [],
    schooldetails: useSelector(state => state.schooldetails) || []
  };

  const loadInitialData = async () => {
    if (!currentSchool?.schoolID) return;

    setLoading(true);
    setError(null);

    try {
      // Initialize chart of accounts with real data mapping if not exists
      let accounts = await accountingService.getChartOfAccounts(currentSchool?.schoolID);
      if (!accounts || accounts.length === 0) {
        // Initialize with default accounts mapped to real data
        accounts = await realDataAccountingService.initializeChartOfAccounts(
          currentSchool?.schoolID,
          admin?.userId || 'system',
          admin?.name || 'System'
        );
      }

      // Load financial periods
      const periods = await accountingService.getFinancialPeriods(currentSchool?.schoolID);
      
      dispatch(setChartOfAccounts(accounts));
      dispatch(setFinancialPeriods(periods));
    } catch (err) {
      setError('Failed to load accounting data');
      console.error('Accounting data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    dispatch(setSelectedTab(newValue));
  };

  const handleRefresh = () => {
    loadInitialData();
  };

  const accountingTabs = [
    {
      label: 'Dashboard',
      icon: <BarChartIcon />,
      component: <AccountingDashboard />
    },
    {
      label: 'Comprehensive View',
      icon: <DataUsageIcon />,
      component: <ComprehensiveAccountingDashboard />
    },
    {
      label: 'Data Debug',
      icon: <DataUsageIcon />,
      component: <DataDebugger />
    },
    {
      label: 'Data Test',
      icon: <AssessmentIcon />,
      component: <DataTest />
    },
    {
      label: 'Data Test Component',
      icon: <DataUsageIcon />,
      component: <DataTestComponent />
    },
    {
      label: 'Data Initializer',
      icon: <DataUsageIcon />,
      component: <DataInitializer />
    },
    {
      label: 'Data Loader',
      icon: <RefreshIcon />,
      component: <DataLoader />
    },
    {
      label: 'API Tester',
      icon: <DataUsageIcon />,
      component: <ApiTester />
    },
    {
      label: 'Data Inspector',
      icon: <DataUsageIcon />,
      component: <DataInspector />
    },
    {
      label: 'Integration',
      icon: <SyncIcon />,
      component: <AccountingIntegration />
    },
    {
      label: 'Trial Balance',
      icon: <AssessmentIcon />,
      component: <TrialBalance />
    },
    {
      label: 'Balance Sheet',
      icon: <AccountBalanceIcon />,
      component: <BalanceSheet />
    },
    {
      label: 'Income Statement',
      icon: <TrendingUpIcon />,
      component: <IncomeStatement />
    },
    {
      label: 'Cash Flow Statement',
      icon: <TimelineIcon />,
      component: <CashFlowStatement />
    },
    {
      label: 'General Ledger',
      icon: <DescriptionIcon />,
      component: <GeneralLedger />
    },
    {
      label: 'Journal Entries',
      icon: <ReceiptIcon />,
      component: <JournalEntries />
    }
  ];

  const quickActions = [
    {
      title: 'Comprehensive View',
      description: 'Complete financial overview covering all aspects',
      icon: <DataUsageIcon />,
      color: '#9c27b0',
      action: () => dispatch(setSelectedTab(1)) // Comprehensive View
    },
    {
      title: 'Trial Balance',
      description: 'View account balances and verify debits equal credits',
      icon: <AssessmentIcon />,
      color: '#1976d2',
      action: () => dispatch(setSelectedTab(6)) // Trial Balance
    },
    {
      title: 'Balance Sheet',
      description: 'Assets, liabilities, and equity statement',
      icon: <AccountBalanceIcon />,
      color: '#2e7d32',
      action: () => dispatch(setSelectedTab(7)) // Balance Sheet
    },
    {
      title: 'Income Statement',
      description: 'Revenue, expenses, and profit/loss statement',
      icon: <TrendingUpIcon />,
      color: '#f57c00',
      action: () => dispatch(setSelectedTab(8)) // Income Statement
    },
    {
      title: 'Cash Flow Statement',
      description: 'Track cash inflows and outflows',
      icon: <TimelineIcon />,
      color: '#7b1fa2',
      action: () => dispatch(setSelectedTab(9)) // Cash Flow
    },
    {
      title: 'General Ledger',
      description: 'Complete transaction history by account',
      icon: <DescriptionIcon />,
      color: '#d32f2f',
      action: () => dispatch(setSelectedTab(10)) // General Ledger
    },
    {
      title: 'Journal Entries',
      description: 'Record and manage financial transactions',
      icon: <ReceiptIcon />,
      color: '#1976d2',
      action: () => dispatch(setSelectedTab(11)) // Journal Entries
    }
  ];

  const additionalActions = [
    {
      title: 'Overdue & Defaulters',
      description: 'Manage outstanding fees and defaulters',
      icon: <WarningIcon />,
      color: '#d32f2f',
      items: [
        'Overdue Fees Report',
        'Defaulters List',
        'Payment History',
        'Collection Reports'
      ]
    },
    {
      title: 'Analytics & Forecasting',
      description: 'Financial forecasting and analytics',
      icon: <TimelineIcon />,
      color: '#f57c00',
      items: [
        'Financial Dashboard',
        'Budget Planning',
        'Forecasting',
        'Cash Reports'
      ]
    },
    {
      title: 'Audit & Compliance',
      description: 'Audit trail and compliance features',
      icon: <SecurityIcon />,
      color: '#9c27b0',
      items: [
        'Audit Trail',
        'GES Reports',
        'Auditor Reports',
        'Compliance Check'
      ]
    },
    {
      title: 'Export & Print',
      description: 'Download and print reports',
      icon: <PrintIcon />,
      color: '#388e3c',
      items: [
        'PDF Reports',
        'Excel Reports',
        'Print Reports',
        'Email Reports'
      ]
    }
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading accounting system...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Accounting System
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Quick Actions Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={action.action}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: action.color, mr: 1 }}>
                    {action.icon}
                  </Box>
                  <Typography variant="h6" component="div">
                    {action.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {action.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    action.action();
                  }}
                >
                  View Report
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Content Tabs */}
      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {accountingTabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
      </Box>

        <Box sx={{ p: 3 }}>
          {accountingTabs[selectedTab]?.component}
                      </Box>
          </Paper>

      {/* System Status */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          System Status
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Chart of Accounts
                  </Typography>
                </Box>
                <Typography variant="h6">
                  {chartOfAccounts?.length || 0} Accounts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Financial Periods
                  </Typography>
                </Box>
                <Typography variant="h6">
                  {financialPeriods?.length || 0} Periods
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Reports Available
                  </Typography>
                </Box>
                <Typography variant="h6">
                  12+ Reports
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Compliance
                  </Typography>
                </Box>
                <Typography variant="h6">
                  GES Ready
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Accounting;